import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FilterRow, TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { sitesApi, type SiteFormData, type SiteRecord, type SitesMeta } from '../services/sites';

const EMPTY_FORM: SiteFormData = {
  parentId: null,
  name: '',
  siteTypeItemId: null,
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  country: 'SE',
  lat: '',
  lng: '',
  notes: '',
  isActive: true,
};

function buildSitePath(site: SiteRecord, byId: Map<number, SiteRecord>): string {
  const parts: string[] = [site.name];
  let currentParentId = site.parentId;
  const visited = new Set<number>();

  while (currentParentId) {
    if (visited.has(currentParentId)) {
      break;
    }
    visited.add(currentParentId);
    const parent = byId.get(currentParentId);
    if (!parent) {
      break;
    }
    parts.unshift(parent.name);
    currentParentId = parent.parentId;
  }

  return parts.join(' / ');
}

export function SitesPage() {
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [meta, setMeta] = useState<SitesMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editing, setEditing] = useState<SiteRecord | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SiteFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { canRead, canWrite } = useAuth();

  if (!canRead('ledger')) {
    return <Navigate to="/" replace />;
  }

  const loadAll = useCallback(async () => {
    try {
      const [sitesRes, metaRes] = await Promise.all([sitesApi.list(true), sitesApi.meta()]);
      setSites(sitesRes.sites);
      setMeta(metaRes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte hämta platser');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const resetForm = () => {
    setEditing(null);
    setShowForm(false);
    setForm(EMPTY_FORM);
  };

  const sitesById = useMemo(() => new Map(sites.map((site) => [site.id, site])), [sites]);

  const parentOptions = useMemo(
    () =>
      sites
        .filter((site) => site.isActive)
        .map((site) => ({
          id: site.id,
          path: buildSitePath(site, sitesById),
        }))
        .sort((a, b) => a.path.localeCompare(b.path, 'sv')),
    [sites, sitesById]
  );

  const filteredSites = useMemo(() => {
    const needle = filter.toLowerCase();
    return [...sites]
      .filter((site) => {
        const matchesText =
          !filter ||
          site.name.toLowerCase().includes(needle) ||
          (site.parentName ?? '').toLowerCase().includes(needle) ||
          (site.siteTypeLabel ?? '').toLowerCase().includes(needle) ||
          (site.city ?? '').toLowerCase().includes(needle);
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' ? !!site.isActive : !site.isActive);
        return matchesText && matchesStatus;
      })
      .sort((a, b) => buildSitePath(a, sitesById).localeCompare(buildSitePath(b, sitesById), 'sv'));
  }, [filter, sites, sitesById, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = async (site: SiteRecord) => {
    try {
      const detail = await sitesApi.get(site.id);
      setEditing(detail);
      setForm({
        parentId: detail.parentId,
        name: detail.name,
        siteTypeItemId: detail.siteTypeItemId,
        address1: detail.address1 ?? '',
        address2: detail.address2 ?? '',
        postalCode: detail.postalCode ?? '',
        city: detail.city ?? '',
        country: detail.country ?? 'SE',
        lat: detail.lat === null ? '' : String(detail.lat),
        lng: detail.lng === null ? '' : String(detail.lng),
        notes: detail.notes ?? '',
        isActive: !!detail.isActive,
      });
      setShowForm(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda plats');
    }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Namn krävs');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await sitesApi.update(editing.id, form);
        toast.success('Plats uppdaterad');
      } else {
        await sitesApi.create(form);
        toast.success('Plats skapad');
      }
      resetForm();
      await loadAll();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara plats');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (site: SiteRecord) => {
    try {
      await sitesApi.delete(site.id);
      toast.success('Plats inaktiverad');
      if (editing?.id === site.id) {
        resetForm();
      }
      await loadAll();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte inaktivera plats');
    }
  };

  if (loading || !meta) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Platser</h1>
        <WithWrite moduleCode="ledger">
          <button type="button" onClick={openCreate} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover">
            Ny plats
          </button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Filtrera på namn, förälder, typ eller ort"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="w-80"
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-40">
          <option value="active">Aktiva</option>
          <option value="inactive">Inaktiva</option>
          <option value="all">Alla</option>
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Namn</th>
            <th className="px-2 py-1.5 text-left font-medium">Förälder</th>
            <th className="px-2 py-1.5 text-left font-medium">Typ</th>
            <th className="px-2 py-1.5 text-left font-medium">Ort</th>
            <th className="px-2 py-1.5 text-left font-medium">Koordinater</th>
            <th className="px-2 py-1.5 text-left font-medium">Status</th>
            <WithWrite moduleCode="ledger">
              <th className="px-2 py-1.5 text-left font-medium">Åtgärder</th>
            </WithWrite>
          </tr>
          <FilterRow>
            <td colSpan={canWrite('ledger') ? 7 : 6} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredSites.map((site) => (
            <tr key={site.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-2 py-1 font-medium">{buildSitePath(site, sitesById)}</td>
              <td className="px-2 py-1">{site.parentName ?? '-'}</td>
              <td className="px-2 py-1">{site.siteTypeLabel ?? '-'}</td>
              <td className="px-2 py-1">{site.city ?? '-'}</td>
              <td className="px-2 py-1">
                {site.lat !== null && site.lng !== null ? `${site.lat}, ${site.lng}` : '-'}
              </td>
              <td className="px-2 py-1">{site.isActive ? 'Aktiv' : 'Inaktiv'}</td>
              <WithWrite moduleCode="ledger">
                <td className="px-2 py-1">
                  <button type="button" onClick={() => openEdit(site)} className="mr-2 text-primary hover:underline">
                    Redigera
                  </button>
                  {site.isActive ? (
                    <button type="button" onClick={() => handleDeactivate(site)} className="text-red-600 hover:underline">
                      Inaktivera
                    </button>
                  ) : null}
                </td>
              </WithWrite>
            </tr>
          ))}
        </tbody>
      </TableWrapper>

      {filteredSites.length === 0 && <p className="text-sm text-gray-500">Inga platser eller inga träffar.</p>}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <h2 className="text-base font-semibold">{editing ? 'Redigera plats' : 'Ny plats'}</h2>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Namn *</label>
                  <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Platstyp</label>
                  <select
                    value={form.siteTypeItemId ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, siteTypeItemId: event.target.value ? Number(event.target.value) : null }))}
                    className="w-full"
                  >
                    <option value="">-</option>
                    {meta.siteTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Förälder</label>
                  <select
                    value={form.parentId ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, parentId: event.target.value ? Number(event.target.value) : null }))}
                    className="w-full"
                  >
                    <option value="">Ingen förälder</option>
                    {parentOptions
                      .filter((parent) => !editing || parent.id !== editing.id)
                      .map((parent) => (
                        <option key={parent.id} value={parent.id}>
                          {parent.path}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Land</label>
                  <input value={form.country ?? ''} onChange={(event) => setForm((current) => ({ ...current, country: event.target.value }))} className="w-full" maxLength={2} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Adress 1</label>
                  <input value={form.address1 ?? ''} onChange={(event) => setForm((current) => ({ ...current, address1: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Adress 2</label>
                  <input value={form.address2 ?? ''} onChange={(event) => setForm((current) => ({ ...current, address2: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Postnummer</label>
                  <input value={form.postalCode ?? ''} onChange={(event) => setForm((current) => ({ ...current, postalCode: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Ort</label>
                  <input value={form.city ?? ''} onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Lat</label>
                  <input value={form.lat ?? ''} onChange={(event) => setForm((current) => ({ ...current, lat: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Lng</label>
                  <input value={form.lng ?? ''} onChange={(event) => setForm((current) => ({ ...current, lng: event.target.value }))} className="w-full" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea value={form.notes ?? ''} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[120px] w-full" />
              </div>

              {editing && (
                <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
                  <input type="checkbox" checked={!!form.isActive} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))} />
                  Aktiv
                </label>
              )}

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={handleSave} disabled={saving} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50">
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button type="button" onClick={resetForm} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
